import "./ClientSelectableTable.css";
import "./MessagingButtons.css";
import { formatDateTime } from "../../../utils/DateTime";
import MessageStatusBadge from "./MessageStatusBadge";

export interface SelectableClientRow {
  id: string;
  clientId: string;
  fullName: string;
  phone: string;
}

export interface ClientSelectableTableProps {
  rows: SelectableClientRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  selectedIds: Set<string>;
  sentStatus: Record<string, number>;
  dueAtById?: Map<string, string | null>;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onToggle: (clientId: string) => void;
  onSelectPage: (clientIds: string[]) => void;
  onClearSelection: () => void;
}

const ClientSelectableTable = ({
  rows,
  totalCount,
  page,
  pageSize,
  onPageChange,
  selectedIds,
  sentStatus,
  dueAtById,
  loading = false,
  error = null,
  onRetry,
  onToggle,
  onSelectPage,
  onClearSelection,
}: ClientSelectableTableProps) => {
  if (loading) {
    return <div className="msg-table-empty">Loading…</div>;
  }

  if (error) {
    return (
      <div className="msg-table-empty">
        <p>{error}</p>
        {onRetry && (
          <button className="msg-btn msg-btn--secondary" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="msg-table-empty">
        No clients match the current filter.
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, totalCount);

  const allOnPageSelected =
    rows.length > 0 && rows.every((row) => selectedIds.has(row.clientId));
  const someOnPageSelected = rows.some((row) =>
    selectedIds.has(row.clientId),
  );

  const togglePage = () => {
    if (allOnPageSelected) {
      const selectedOnPage = rows
        .filter((row) => selectedIds.has(row.clientId))
        .map((row) => row.clientId);
      onSelectPage(selectedOnPage);
    } else {
      const unselectedOnPage = rows
        .filter((row) => !selectedIds.has(row.clientId))
        .map((row) => row.clientId);
      onSelectPage(unselectedOnPage);
    }
  };

  return (
    <div className="msg-table">
      <div className="msg-table__wrap">
        <table className="msg-table__grid">
          <thead>
            <tr>
              <th className="msg-table__check">
                <input
                  type="checkbox"
                  aria-label="Select all on this page"
                  checked={allOnPageSelected}
                  ref={(el) => {
                    if (el)
                      el.indeterminate = someOnPageSelected && !allOnPageSelected;
                  }}
                  onChange={togglePage}
                />
              </th>
              <th>Client</th>
              <th>Phone</th>
              <th>Follow-up due</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const dueAt = dueAtById?.get(row.id) ?? null;
              const overdue = dueAt
                ? new Date(dueAt).getTime() < Date.now()
                : false;
              const noPhone = !row.phone?.trim();
              const status = sentStatus[row.clientId];

              return (
                <tr
                  key={row.id}
                  className={
                    selectedIds.has(row.clientId) ? "msg-table__row--selected" : ""
                  }
                >
                  <td className="msg-table__check">
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.fullName}`}
                      checked={selectedIds.has(row.clientId)}
                      disabled={noPhone}
                      onChange={() => onToggle(row.clientId)}
                    />
                    {noPhone && (
                      <span
                        className="msg-table__nophone"
                        title="No phone on file — backend rejects messages without a phone"
                      >
                        ⚠
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="msg-table__name">
                      <span className="msg-table__avatar">
                        {row.fullName.charAt(0).toUpperCase()}
                      </span>
                      {row.fullName}
                    </div>
                  </td>
                  <td>{row.phone || "—"}</td>
                  <td>
                    {dueAt ? (
                      <span
                        className={`msg-table__due ${
                          overdue ? "msg-table__due--overdue" : ""
                        }`}
                      >
                        {formatDateTime(dueAt)}
                        {overdue && " · overdue"}
                      </span>
                    ) : (
                      <span className="msg-table__muted">—</span>
                    )}
                  </td>
                  <td>
                    {status !== undefined ? (
                      <MessageStatusBadge status={status} />
                    ) : (
                      <span className="msg-table__muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="msg-table__footer">
        <span className="msg-table__info">
          Showing {start}–{end} of {totalCount}
          {selectedIds.size > 0 && (
            <>
              {" · "}
              <strong>{selectedIds.size} selected</strong>
              <button
                className="msg-btn msg-btn--link"
                onClick={onClearSelection}
              >
                Clear
              </button>
            </>
          )}
        </span>
        <div className="msg-table__pager">
          <button
            className="msg-btn msg-btn--secondary"
            disabled={safePage <= 1}
            onClick={() => onPageChange(safePage - 1)}
          >
            Previous
          </button>
          <span className="msg-table__page">
            Page {safePage} of {totalPages}
          </span>
          <button
            className="msg-btn msg-btn--secondary"
            disabled={safePage >= totalPages}
            onClick={() => onPageChange(safePage + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientSelectableTable;
